import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { 
  Store, 
  Smartphone, 
  QrCode, 
  Gift, 
  TrendingUp, 
  Shield, 
  Zap, 
  Users, 
  BarChart,
  Star,
  CheckCircle2,
  ArrowRight,
  Menu,
  X,
  Send
} from 'lucide-react';

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    businessName: ''
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);

    try {
      // Add form submission to Firestore
      // The document structure is designed for Firebase Email Extension
      await addDoc(collection(db, 'contactSubmissions'), {
        // Contact form data
        name: formData.name,
        email: formData.email,
        businessName: formData.businessName,
        submittedAt: serverTimestamp(),
        
        // Email configuration for Firebase Email Extension
        to: 'loyalcraftperth@gmail.com',
        message: {
          subject: `New LoyalCraft Inquiry from ${formData.name}`,
          text: `
New contact form submission from LoyalCraft website:

Name: ${formData.name}
Email: ${formData.email}
Business Name: ${formData.businessName}

Please respond to this inquiry at your earliest convenience.
          `,
          html: `
            <div style="font-family: system-ui, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
              <h2 style="color: #F27C3A;">New LoyalCraft Inquiry</h2>
              <p>You have received a new contact form submission from your website:</p>
              
              <div style="background: #f5f5f5; padding: 20px; border-radius: 8px; margin: 20px 0;">
                <p><strong>Name:</strong> ${formData.name}</p>
                <p><strong>Email:</strong> <a href="mailto:${formData.email}">${formData.email}</a></p>
                <p><strong>Business Name:</strong> ${formData.businessName}</p>
              </div>
              
              <p style="color: #666; font-size: 14px;">Submitted at: ${new Date().toLocaleString()}</p>
            </div>
          `
        }
      });

      setFormSubmitting(false);
      setFormSubmitted(true);
      console.log('Form submitted successfully to Firebase');
      
      // Reset form after 2 seconds
      setTimeout(() => {
        setContactDialogOpen(false);
        setFormSubmitted(false);
        setFormData({ name: '', email: '', businessName: '' });
      }, 2000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormSubmitting(false);
      alert('There was an error submitting the form. Please try again.');
    }
  };

  const openContactForm = () => {
    setContactDialogOpen(true);
  };

  const features = [
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: "All Cards in One App",
      description: "Customers carry all their loyalty cards digitally. No more wallet clutter.",
      audience: "customer"
    },
    {
      icon: <QrCode className="w-8 h-8" />,
      title: "QR Code Scanning",
      description: "Quick and contactless. Scan to earn points or stamps instantly.",
      audience: "both"
    },
    {
      icon: <Store className="w-8 h-8" />,
      title: "Easy Setup for Businesses",
      description: "Launch your loyalty program in minutes. No hardware required.",
      audience: "business"
    },
    {
      icon: <Gift className="w-8 h-8" />,
      title: "Customizable Rewards",
      description: "Choose stamp-based or points-based programs that fit your business.",
      audience: "business"
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Real-Time Tracking",
      description: "See your progress and rewards update instantly with every purchase.",
      audience: "customer"
    },
    {
      icon: <BarChart className="w-8 h-8" />,
      title: "Analytics Dashboard",
      description: "Track customer engagement and program performance with detailed insights.",
      audience: "business"
    }
  ];

  const benefits = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Secure & Reliable",
      description: "Bank-level encryption keeps customer data safe and protected."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Lightning Fast",
      description: "Transactions complete in seconds. No waiting, no friction."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Increase Customer Retention",
      description: "Keep customers coming back with engaging loyalty programs."
    }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "Owner, The Daily Grind Cafe",
      content: "LoyalCraft transformed how we engage with our regulars. Setup took 10 minutes, and we've seen a 40% increase in repeat visits.",
      rating: 5
    },
    {
      name: "Marcus Johnson",
      role: "Regular Customer",
      content: "Finally, no more lost punch cards! I have all my favorite shops' loyalty programs in one place. So convenient.",
      rating: 5
    },
    {
      name: "Emma Rodriguez",
      role: "Salon Manager, Bella Beauty",
      content: "Our clients love the digital experience. The analytics help us understand their preferences better than ever.",
      rating: 5
    }
  ];

  const pricingPlans = [
    {
      name: "Pop-Up",
      price: 15,
      scansIncluded: "200",
      perScanRate: "$0.30",
      recommended: "0-200 scans/mo",
      description: "Perfect for new businesses and pop-up shops",
      popular: false
    },
    {
      name: "Bistro",
      price: 35,
      scansIncluded: "600",
      perScanRate: "$0.20",
      recommended: "201-600 scans/mo",
      description: "Ideal for cafes and small restaurants",
      popular: true
    },
    {
      name: "Flagship",
      price: 75,
      scansIncluded: "2,000",
      perScanRate: "$0.10",
      recommended: "601-2,000 scans/mo",
      description: "Great for established businesses",
      popular: false
    },
    {
      name: "Empire",
      price: 150,
      scansIncluded: "2,000",
      perScanRate: "$0.05",
      recommended: "2,000+ scans/mo",
      description: "For high-volume multi-location businesses",
      popular: false
    }
  ];

  const faqs = [
    {
      question: "How does LoyalCraft work?",
      answer: "LoyalCraft is a digital platform that replaces physical loyalty cards. Businesses create customizable loyalty programs, and customers collect stamps or points by scanning QR codes at checkout. Everything is tracked in real-time through our mobile app."
    },
    {
      question: "Do I need special hardware to use LoyalCraft?",
      answer: "No! That's the beauty of LoyalCraft. You only need a smartphone or tablet to display QR codes. No expensive card readers or specialized equipment required."
    },
    {
      question: "Can I customize my loyalty program?",
      answer: "Absolutely! You can choose between stamp-based programs (e.g., buy 10 coffees, get 1 free) or points-based systems. Customize rewards, branding, and program rules to match your business."
    },
    {
      question: "Is there a limit to how many loyalty cards I can have?",
      answer: "For customers, there's no limit! Add as many businesses as you like to your digital wallet. All your loyalty programs in one convenient app."
    },
    {
      question: "How do customers redeem rewards?",
      answer: "When a customer reaches their reward threshold, it appears in their app. They simply show their phone at checkout to redeem. The merchant scans a confirmation QR code, and it's done!"
    },
    {
      question: "Is my data secure?",
      answer: "Yes! We use bank-level encryption and follow industry best practices for data security. Your information is stored securely and never shared without permission."
    }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Header */}
      <nav className="nav-header">
        <div className="flex items-center justify-between w-full px-4">
          <div className="flex items-center space-x-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_loyalty-rewards-45/artifacts/huy3me2a_loyalcraft-logo.png" 
              alt="LoyalCraft Logo" 
              className="h-10 w-10 object-contain"
            />
            <span className="font-bold text-xl" style={{ color: 'var(--text-primary)' }}>LoyalCraft</span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <button onClick={() => scrollToSection('features')} className="nav-link">Features</button>
            <button onClick={() => scrollToSection('how-it-works')} className="nav-link">How it Works</button>
            <button onClick={() => scrollToSection('pricing')} className="nav-link">Pricing</button>
            <button onClick={() => scrollToSection('faq')} className="nav-link">FAQ</button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white border-t border-border-light mt-2 mx-4 rounded-lg shadow-lg">
            <div className="flex flex-col p-4 space-y-2">
              <button onClick={() => scrollToSection('features')} className="nav-link text-left">Features</button>
              <button onClick={() => scrollToSection('how-it-works')} className="nav-link text-left">How it Works</button>
              <button onClick={() => scrollToSection('pricing')} className="nav-link text-left">Pricing</button>
              <button onClick={() => scrollToSection('faq')} className="nav-link text-left">FAQ</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">
            Your Customers' Loyalty,
            <br />
            <span style={{ color: 'var(--accent-primary)' }}>Digitally Rewarded</span>
          </h1>
          <p className="hero-subtitle">
            Replace physical loyalty cards with a modern digital solution. 
            One app for customers, powerful tools for businesses.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-8">
            <Button onClick={openContactForm} className="btn-primary text-lg px-8 py-6">
              Start Free Trial <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
          <p className="caption mt-6">No credit card required • Setup in 5 minutes</p>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-2 mb-4">Everything You Need to Build Loyalty</h2>
            <p className="body-large text-secondary">
              Powerful features for businesses and delightful experiences for customers
            </p>
          </div>

          <div className="ai-grid">
            {features.map((feature, index) => (
              <Card key={index} className="product-card">
                <CardHeader>
                  <div className="w-14 h-14 rounded-full bg-accent-wash flex items-center justify-center mb-4" style={{ color: 'var(--accent-primary)' }}>
                    {feature.icon}
                  </div>
                  <CardTitle className="product-card-title">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="product-card-description">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-6" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-2 mb-4">How LoyalCraft Works</h2>
            <p className="body-large text-secondary">Simple setup, seamless experience</p>
          </div>

          <div className="grid md:grid-cols-3 gap-12 max-w-5xl mx-auto">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center text-white text-2xl font-bold" style={{ background: 'var(--gradient-button)' }}>
                1
              </div>
              <h3 className="heading-3 mb-3">Create Your Program</h3>
              <p className="body-medium text-secondary">
                Sign up and customize your loyalty program in minutes. Choose stamps or points, set rewards, and brand it your way.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center text-white text-2xl font-bold" style={{ background: 'var(--gradient-button)' }}>
                2
              </div>
              <h3 className="heading-3 mb-3">Customers Join</h3>
              <p className="body-medium text-secondary">
                Customers scan your unique QR code to join your program. They add your card to their digital wallet instantly.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center text-white text-2xl font-bold" style={{ background: 'var(--gradient-button)' }}>
                3
              </div>
              <h3 className="heading-3 mb-3">Reward & Repeat</h3>
              <p className="body-medium text-secondary">
                Every purchase earns stamps or points. When they reach the goal, they redeem rewards. Simple, fast, effective.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 px-6">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-2 mb-4">Why Businesses Choose LoyalCraft</h2>
            <p className="body-large text-secondary">
              More than just a loyalty program—it's a growth engine
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6">
                <div className="w-12 h-12 rounded-full bg-accent-wash flex items-center justify-center mx-auto mb-4" style={{ color: 'var(--accent-primary)' }}>
                  {benefit.icon}
                </div>
                <h3 className="heading-3 mb-3">{benefit.title}</h3>
                <p className="body-medium text-secondary">{benefit.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 bg-white border border-border-light rounded-lg p-8 md:p-12 max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="heading-3 mb-6">For Businesses</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                    <span className="body-medium">Increase repeat customer visits by up to 40%</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                    <span className="body-medium">No hardware costs—just your smartphone</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                    <span className="body-medium">Track customer behavior and preferences</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                    <span className="body-medium">Send targeted promotions and updates</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="heading-3 mb-6">For Customers</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                    <span className="body-medium">All loyalty cards in one convenient app</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                    <span className="body-medium">Never lose a punch card again</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                    <span className="body-medium">Track rewards progress in real-time</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                    <span className="body-medium">Get notified when rewards are ready</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-6" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-2 mb-4">Loved by Businesses & Customers</h2>
            <p className="body-large text-secondary">See what people are saying about LoyalCraft</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="product-card">
                <CardHeader>
                  <div className="flex mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-current" style={{ color: 'var(--accent-primary)' }} />
                    ))}
                  </div>
                  <CardDescription className="body-medium text-body italic">
                    "{testimonial.content}"
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>{testimonial.name}</p>
                  <p className="body-small text-secondary">{testimonial.role}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-2 mb-4">Simple, Transparent Pricing</h2>
            <p className="body-large text-secondary">
              Choose the plan that fits your business size. No hidden fees.
            </p>
            <p className="body-small text-muted mt-2">
              Your monthly fee covers scans up to each tier's included amount; the per-scan rate applies only to scans beyond that.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <Card 
                key={index} 
                className={`product-card relative ${plan.popular ? 'border-2' : ''}`}
                style={plan.popular ? { borderColor: 'var(--accent-primary)' } : {}}
              >
                {plan.popular && (
                  <div 
                    className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full text-white text-sm font-semibold"
                    style={{ background: 'var(--gradient-button)' }}
                  >
                    Most Popular
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="heading-3 mb-2">{plan.name}</CardTitle>
                  <div className="mb-4">
                    <span className="text-4xl font-bold" style={{ color: 'var(--text-primary)' }}>
                      ${plan.price}
                    </span>
                    <span className="text-secondary">/month</span>
                  </div>
                  <CardDescription className="body-small text-secondary mb-4">
                    {plan.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start body-small">
                      <CheckCircle2 className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                      <span>Up to <strong>{plan.scansIncluded} scans/mo</strong> included</span>
                    </li>
                    <li className="flex items-start body-small">
                      <CheckCircle2 className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                      <span><strong>{plan.perScanRate}</strong> per additional scan</span>
                    </li>
                    <li className="flex items-start body-small">
                      <CheckCircle2 className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                      <span>QR code generation</span>
                    </li>
                    <li className="flex items-start body-small">
                      <CheckCircle2 className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                      <span>Customer app access</span>
                    </li>
                    <li className="flex items-start body-small">
                      <CheckCircle2 className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                      <span>Analytics dashboard</span>
                    </li>
                  </ul>
                  <div className="text-center p-3 rounded-lg mb-4" style={{ background: 'var(--bg-section)' }}>
                    <p className="caption text-secondary">Recommended for</p>
                    <p className="body-small font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {plan.recommended}
                    </p>
                  </div>
                  <Button 
                    onClick={openContactForm}
                    className={plan.popular ? "btn-primary w-full" : "btn-secondary w-full"}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="body-medium text-secondary">
              Need a custom plan for your enterprise? <button onClick={openContactForm} className="link-text">Contact us</button>
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-6">
        <div className="container max-w-3xl">
          <div className="text-center mb-16">
            <h2 className="heading-2 mb-4">Frequently Asked Questions</h2>
            <p className="body-large text-secondary">Everything you need to know about LoyalCraft</p>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left heading-3 py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="body-medium text-secondary">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6" style={{ background: 'var(--gradient-hero)' }}>
        <div className="container max-w-4xl text-center">
          <h2 className="heading-2 mb-6">Ready to Build Customer Loyalty?</h2>
          <p className="body-large text-secondary mb-8">
            Join thousands of businesses using LoyalCraft to create meaningful customer relationships
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button onClick={openContactForm} className="btn-primary text-lg px-8 py-6">
              Start Free Trial <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
          <p className="caption mt-6">No credit card required • Cancel anytime</p>
        </div>
      </section>

      {/* Contact Form Dialog */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="heading-3">Get Started with LoyalCraft</DialogTitle>
            <DialogDescription className="body-medium text-secondary">
              Fill in your details and we'll get back to you shortly to set up your loyalty program.
            </DialogDescription>
          </DialogHeader>

          {!formSubmitted ? (
            <form onSubmit={handleFormSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="body-medium font-semibold">Name *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="body-medium font-semibold">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="businessName" className="body-medium font-semibold">Business Name *</Label>
                <Input
                  id="businessName"
                  name="businessName"
                  type="text"
                  placeholder="Your business name"
                  value={formData.businessName}
                  onChange={handleInputChange}
                  required
                  className="w-full"
                />
              </div>

              <Button 
                type="submit" 
                className="btn-primary w-full mt-6"
                disabled={formSubmitting}
              >
                {formSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    Submit <Send className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>
            </form>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-accent-wash flex items-center justify-center mx-auto mb-4" style={{ color: 'var(--accent-primary)' }}>
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h3 className="heading-3 mb-2">Thank You!</h3>
              <p className="body-medium text-secondary">
                We've received your information and will contact you soon.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="py-12 px-6 border-t" style={{ borderColor: 'var(--border-light)' }}>
        <div className="container">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_loyalty-rewards-45/artifacts/huy3me2a_loyalcraft-logo.png" 
                  alt="LoyalCraft Logo" 
                  className="h-8 w-8 object-contain"
                />
                <span className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>LoyalCraft</span>
              </div>
              <p className="body-small text-secondary">
                Digital loyalty cards for modern businesses
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Product</h4>
              <ul className="space-y-2">
                <li><a href="#features" className="body-small text-secondary hover:text-primary">Features</a></li>
                <li><a href="#how-it-works" className="body-small text-secondary hover:text-primary">How it Works</a></li>
                <li><a href="#" className="body-small text-secondary hover:text-primary">Pricing</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Company</h4>
              <ul className="space-y-2">
                <li><a href="#" className="body-small text-secondary hover:text-primary">About Us</a></li>
                <li><a href="#" className="body-small text-secondary hover:text-primary">Blog</a></li>
                <li><a href="#" className="body-small text-secondary hover:text-primary">Careers</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Support</h4>
              <ul className="space-y-2">
                <li><a href="#" className="body-small text-secondary hover:text-primary">Help Center</a></li>
                <li><a href="#" className="body-small text-secondary hover:text-primary">Contact Us</a></li>
                <li><a href="#" className="body-small text-secondary hover:text-primary">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t text-center" style={{ borderColor: 'var(--border-light)' }}>
            <p className="body-small text-secondary">
              © 2024 LoyalCraft. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
